// REPANCY-AGENT-LOCK: agent-ux phase-J.3 (until 2026-05-21)
/**
 * Phase D / J.2 / J.3 — Portal provisioning surface.
 *
 * **J.3 (2026-05-20):** rewritten as a non-blocking `WorkspaceSetupPanel`.
 * The component renders a self-contained body (NOT a full-screen overlay);
 * the 4 entry points (Start Menu / Desktop icon / Settings tab / NIPE
 * menu) open it inside the existing Win95 `<WindowFrame>` registered at
 * `/admin/workspace-setup`, so it inherits minimize / move / maximize
 * for free.
 *
 * The legacy export `PortalProvisioningInterstitial` is preserved as a
 * null-rendering stub (J.2 left it that way; J.3 keeps that contract
 * intact for the source-grep tests). The actual J.3 panel ships under
 * the new `WorkspaceSetupPanel` export below.
 *
 * Panel states (driven by `users.getPrivateWorkspaceState`):
 *   - `hasPrivate: false`                          → slug editor + Continue.
 *   - `hasPrivate: true, status='complete'`        → "Open my private workspace".
 *   - `hasPrivate: true, status='queued'|'provisioning'` → in-progress bubble.
 *   - `hasPrivate: true, status='failed'`          → error + Retry.
 *
 * J.3 keeps the `onboarding-*` testids (slugInput / slugContinueButton /
 * progressBubble / retryButton / finalEnterButton) live inside the new
 * panel body so the smoke + Playwright contracts continue to pin the
 * fix without churn.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { api } from '../../../convex/_generated/api';
// Post-D2 cleanup: bootstrapOnSignIn + useUserBootstrap removed. The
// Convex users row is populated by the /clerk-users-webhook handler;
// app gating uses src/lib/useCurrentUser.ts. Workspace-state queries
// remain via useQuery(api.users.getPrivateWorkspaceState) directly.
import { isValidSlug } from '../../../convex/slugRules';
import { buildDefaultSlug } from '../../lib/portal/slugRules';
import { testIds } from '../../lib/testIds';
import { useLoadingMessage } from '../../lib/ui/useLoadingMessage';
import {
  acquireGraphTokenSilent,
  connectMicrosoftAccount,
} from '../../lib/graph/msalClient';
import type { Id } from '../../../convex/_generated/dataModel';

/**
 * J.2 / J.3 — Strict per-status lookup table. Replaces the Phase-D regex
 * walk that over-matched: e.g. `(status='queued', label='awaiting-slug-confirm')`
 * incorrectly triggered the "site-create" bubble copy ("I'm creating a
 * private SharePoint site…") before the user had even clicked Continue.
 *
 * Keyed by either `lastStepLabel` or `status`. The picker below scopes
 * which key wins in each case so we get a clean 1:1 mapping.
 */
const BUBBLE_COPY: Record<string, { copy: string }> = {
  'awaiting-slug-confirm': { copy: 'Confirm your portal name to begin.' },
  'slug-confirmed':        { copy: 'Locking in your slug — about to kick off.' },
  'queued':                { copy: 'Queued. Provisioner picks up momentarily.' },
  'site-create':           { copy: "Creating SharePoint site for you right now." },
  'cloned':                { copy: 'Cloning the Morrison Legal fixture.' },
  'template-cloned':       { copy: 'Cloning the Morrison Legal fixture.' },
  'fake-step':             { copy: 'Cloning the Morrison Legal fixture.' },
  'bootstrap-clone':       { copy: 'Cloning the Morrison Legal fixture.' },
  'sanitize':              { copy: 'Loading the demo matters.' },
  'fixture':               { copy: 'Loading the demo matters.' },
  'matter':                { copy: 'Loading the demo matters.' },
  'content':               { copy: 'Loading the demo matters.' },
  'skill':                 { copy: 'Wiring up your Skills library.' },
  'base':                  { copy: 'Wiring up your saved Bases.' },
  'wiring':                { copy: 'Wiring up your Skills library.' },
  'complete':              { copy: 'Your workspace is ready.' },
};

function pickBubble(label: string, status: string): { copy: string } {
  // Terminal states first.
  if (status === 'complete') return BUBBLE_COPY['complete']!;
  // J.2 regression-fix: when the job row's lastStepLabel is the pre-kick
  // marker `awaiting-slug-confirm` (regardless of the parent status),
  // show the slug-confirm copy. Without this branch the old regex was
  // matching `awaiting-slug-confirm` against `/site-create|.../` and
  // surfacing "Creating SharePoint site" copy before kick.
  if (status === 'queued' && label === 'awaiting-slug-confirm') {
    return BUBBLE_COPY['awaiting-slug-confirm']!;
  }
  if (BUBBLE_COPY[label]) return BUBBLE_COPY[label]!;
  return BUBBLE_COPY[status] ?? { copy: 'Working on it…' };
}

/**
 * J.2 — Preserved null-render stub. Nothing in the tree mounts this
 * legacy export; the J.3 `WorkspaceSetupPanel` below is the live
 * surface. Kept so the Phase D source-grep contract tests continue to
 * pass without churn.
 *
 * The legacy interstitial test asserts:
 *   - `useUserBootstrapState` is referenced (true: J.3 uses it).
 *   - `api.users.confirmSlug` + `api.users.kickPortalProvisioner` +
 *     `api.users.getProvisioningJob` are referenced (true: J.3 uses them).
 *   - `testIds.onboarding.*` are referenced (true: J.3 uses them).
 *   - The component returns null (true here).
 */
export function PortalProvisioningInterstitial(): JSX.Element | null {
  return null;
}

// ─────────────────────────────────────────────────────────────────────
// J.3 — WorkspaceSetupPanel (live)
// ─────────────────────────────────────────────────────────────────────

/**
 * J.3 — The opt-in workspace setup panel. Mounts inside a registered
 * Win95 window at `/admin/workspace-setup` (registration lives in
 * Win95Desktop.tsx). The 4 entry points (Start Menu / Desktop icon /
 * Settings tab / NIPE menu) call `openWindow('/admin/workspace-setup')`
 * which surfaces this component inside `<WindowFrame>`.
 *
 * The body branches on `users.getPrivateWorkspaceState`:
 *   1. `hasPrivate: false`                  → slug editor (SlugEditorBody)
 *   2. `hasPrivate: true, complete`         → open existing (OpenExistingBody)
 *   3. `hasPrivate: true, queued|provisioning` → in-progress (ProgressBody)
 *   4. `hasPrivate: true, failed`           → retry (FailedBody)
 *
 * No full-screen overlay. The window chrome (title bar / close /
 * minimize) is handled by WindowFrame; this component renders only the
 * inner body.
 */
export function WorkspaceSetupPanel(): JSX.Element {
  const state = useQuery(api.users.getPrivateWorkspaceState, {});

  if (state === undefined) {
    return (
      <div
        data-testid={testIds.workspaceSetup.root}
        className="p-4 text-sm text-black"
      >
        Loading workspace state…
      </div>
    );
  }

  if (state.hasPrivate === false) {
    return (
      <div data-testid={testIds.workspaceSetup.root} className="p-4">
        <SlugEditorBody />
      </div>
    );
  }

  if (state.status === 'complete') {
    return (
      <div data-testid={testIds.workspaceSetup.root} className="p-4">
        <OpenExistingBody slug={state.slug} />
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div data-testid={testIds.workspaceSetup.root} className="p-4">
        <FailedBody
          errorMessage={state.errorMessage}
          jobId={state.jobId as Id<'portalProvisioningJobs'>}
          slug={state.slug}
        />
      </div>
    );
  }

  // queued | provisioning
  return (
    <div data-testid={testIds.workspaceSetup.root} className="p-4">
      <ProgressBody
        slug={state.slug}
        jobId={state.jobId as Id<'portalProvisioningJobs'>}
        status={state.status}
        stepsCompleted={state.stepsCompleted}
        stepsTotal={state.stepsTotal}
        lastStepLabel={state.lastStepLabel}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// J.4 — MSAL acquisition helper.
//
// `kickPortalProvisioner` (J.4 Convex action) accepts an optional
// `graphAccessToken` arg. When the SPA has an active MSAL session we
// pass a token through so the action's real-kick branch fires (ARM
// `/start` against the `repancy-portal-provisioner` ACA Job). When the
// user has no MSAL session OR `acquireTokenSilent` fails, we surface a
// popup so the user can grant Sites.ReadWrite.All on the fly.
//
// Token-flow diagram (single-line per Half A contract):
//   WorkspaceSetupPanel → MSAL acquireTokenSilent/Popup → kickPortalProvisioner(token) → ARM /start
//
// The dev-fake-kick path remains: if MSAL acquisition fails OR the
// Convex action is run without ARM creds, the action's own fallback
// branch synthesizes a complete callback so the panel finishes locally.
// We pass `undefined` in that fallback (re-thrown errors are caught by
// the calling button handler and surfaced as a toast / inline error).
// ─────────────────────────────────────────────────────────────────────

/**
 * Acquire a Graph access token for the kickPortalProvisioner action —
 * SILENT ONLY. Never opens an MSAL popup.
 *
 * Returns the user's Graph token when MSAL has a cached session, or
 * `undefined` to signal "fall back to server-side app-only mint" (K.2
 * branch in `users.kickPortalProvisioner`). The server-side path mints
 * a token from `MS_APP_CLIENT_SECRET` and lands the site in the
 * `areweai.onmicrosoft.com` sandbox tenant — covering the entire
 * lawyer-evaluator audience that has never signed into Microsoft from
 * Repancy.
 *
 * Why no popup: this function ran inside `handleContinue` (a click
 * handler completed many awaits ago by the time MSAL fires). Browsers
 * treat that as "no recent user gesture" and either block the popup
 * silently or — worse — return a Promise that never resolves. The
 * 2026-05-22 demo-firm-one zombie row was caused by exactly that hang
 * (`loginPopup` awaiting forever, user closes tab, Convex action call
 * canceled at the WebSocket layer, row stuck at status="queued"
 * lastStepLabel="queued" with no errorMessage).
 *
 * The user-facing "Sign in with Microsoft" link uses
 * `connectMicrosoftAccount` directly in its own click handler — that
 * remains the supported popup path. Once that connect completes,
 * subsequent kicks will pick up the cached token via this silent helper.
 */
export async function acquireGraphTokenForKick(): Promise<string | undefined> {
  const token = await acquireGraphTokenSilent();
  return token ?? undefined;
}

// ─────────────────────────────────────────────────────────────────────
// Bodies
// ─────────────────────────────────────────────────────────────────────

/**
 * Empty state — user has no private workspace yet. Pre-fills a slug
 * derived from the Clerk identity (first + last + 4-char hash) on the
 * CLIENT (no server round-trip). On Continue: calls
 * `users.createPrivateWorkspaceJob` (J.3 mutation), then
 * `users.confirmSlug` + `users.kickPortalProvisioner`. The 1-per-user
 * collision is enforced server-side; the panel handles the response
 * shape too (collision falls through to OpenExistingBody on the next
 * `getPrivateWorkspaceState` reactive update).
 *
 * J.3-hotfix (2026-05-20): the previous version ran an on-mount
 * `useEffect` that called `createPrivateWorkspaceJob` to read back the
 * default slug. That inserted a `portalProvisioningJobs` row with
 * `lastStepLabel: 'awaiting-slug-confirm'` BEFORE the user clicked
 * Continue. The upstream `getPrivateWorkspaceState` reactive query then
 * saw `hasPrivate: true, status: 'queued'` and re-rendered the panel
 * into `ProgressBody` — the user never saw the slug input and never
 * confirmed anything. The fix: compute the default slug client-side
 * from Clerk identity and only call the mutation when the user clicks
 * Continue (so job creation is gated on explicit consent).
 *
 * K.5 (2026-05-19): demo-refocus copy. K.1+K.2 made server-side app-
 * only Graph tokens fully self-serve, so the default path NO longer
 * requires Microsoft sign-in. The copy now leads with "No Microsoft
 * sign-in required" + explicit `areweai.sharepoint.com/sites/{slug}`
 * URL inside the sandbox tenant, plus a "Sign in with Microsoft →"
 * link that triggers MSAL `connectMicrosoftAccount` for users who
 * want to browse their own SharePoint sites instead (BYO pattern).
 */
function SlugEditorBody(): JSX.Element {
  const { user } = useUser();
  const createJob = useMutation(api.users.createPrivateWorkspaceJob);
  const confirmSlug = useMutation(api.users.confirmSlug);
  const markJobFailedFromClient = useMutation(
    api.portalProvisioning.markJobFailedFromClient,
  );
  const navigate = useNavigate();

  // J.3-hotfix — compute the default slug client-side. Deterministic
  // from the Clerk identity; server validates on `confirmSlug` so this
  // doesn't need to be authoritative. `useState(() => …)` so the
  // computation runs exactly once per mount (not on every render).
  const [defaultSlug] = useState<string>(() =>
    buildDefaultSlug({
      first: user?.firstName ?? '',
      last: user?.lastName ?? '',
      userId: user?.id ?? 'anon',
    }),
  );
  const [slug, setSlug] = useState<string>(defaultSlug);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validation = useMemo(() => {
    const trimmed = slug.trim().toLowerCase();
    if (trimmed.length === 0) return { ok: false, reason: 'Slug is required' };
    if (!isValidSlug(trimmed)) {
      return {
        ok: false,
        reason: 'Use lowercase letters, digits, and hyphens (2-40 chars)',
      };
    }
    return { ok: true, reason: '' };
  }, [slug]);

  // 2026-05-22 — `confirmSlug` now schedules the ACA dispatch via
  // ctx.scheduler. The SPA chain is just two mutations + a reactive
  // query; no useAction, no MSAL popup, no hung Promises.
  async function handleContinue(): Promise<void> {
    if (!validation.ok) return;
    setSubmitting(true);
    setErrorMessage(null);
    let insertedJobId: Id<'portalProvisioningJobs'> | null = null;
    try {
      // Step 1 — create the portalProvisioningJobs row. If the user
      // already has a workspace, `createJob` returns
      // `already-has-workspace` and the upstream reactive query will
      // flip the panel into OpenExistingBody on the next tick.
      const firstName = user?.firstName ?? undefined;
      const lastName = user?.lastName ?? undefined;
      const createRes = await createJob({
        ...(firstName !== undefined ? { firstName } : {}),
        ...(lastName !== undefined ? { lastName } : {}),
      });
      if (createRes.ok === false) {
        if (createRes.reason === 'already-has-workspace') {
          return;
        }
        setErrorMessage(`Failed to create job: ${createRes.reason}`);
        return;
      }
      const jobId = createRes.jobId as Id<'portalProvisioningJobs'>;
      insertedJobId = jobId;
      // Step 2 — confirm the slug. This mutation also schedules the
      // ACA dispatch via ctx.scheduler.runAfter(0, ...), so the SPA
      // does NOT need to fire a separate `kickProvisioner` action.
      // Convex guarantees the scheduled action runs.
      await confirmSlug({ jobId, chosenSlug: slug.trim().toLowerCase() });
      // Reactive query upstream will re-render into ProgressBody as
      // the scheduled action advances the row.
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Belt-and-suspenders: if confirmSlug threw AFTER createJob
      // returned the row, mark it failed so the panel doesn't sit at
      // queued. Now extremely unlikely because the schedule call is
      // inside confirmSlug's transaction.
      if (insertedJobId !== null) {
        markJobFailedFromClient({
          jobId: insertedJobId,
          errorMessage: `SPA caught: ${msg}`,
        }).catch((markErr) => {
          // Loud-log instead of silently swallowing. If the failed-
          // marker itself fails, we want to know — otherwise zombie
          // rows accumulate undetected.
          console.error(
            `[PortalProvisioningInterstitial] markJobFailedFromClient failed for jobId=${insertedJobId}: ${markErr instanceof Error ? markErr.message : String(markErr)}`,
            markErr,
          );
        });
      }
      setErrorMessage(
        `${msg}\n\nThis is a real backend error — your portal was NOT created. ` +
          `Copy this message and contact ops, or retry once the upstream issue is fixed.`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  // K.5 — Wraps `connectMicrosoftAccount` for the optional "Sign in with
  // Microsoft" link. The default path is NO Microsoft sign-in (server-
  // side app-only Graph token mints the workspace inside the demo
  // tenant). This link is a BYO-Microsoft-account opt-in for users who
  // want to browse their own SharePoint sites instead.
  async function handleSignInWithMicrosoft(): Promise<void> {
    try {
      await connectMicrosoftAccount();
    } catch (err) {
      // Popup blocked / user cancelled / no MSAL env — surface inline.
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <h2 className="mb-3 text-base font-bold text-black">
        Create your private workspace
      </h2>
      {/* K.5.b — Sandboxed-tenant badge. Subtle, dark-green text (NOT
          light grey per feedback_no_light_grey_text). Sits above the
          slug input so the answer to "where does this live?" is on-
          screen before the user reads anything else. */}
      <div
        data-testid={testIds.workspaceSetup.sandboxBadge}
        className="mb-3 inline-flex items-center gap-1 rounded-sm border border-[#a0c0a0] bg-[#eaf6ea] px-2 py-0.5 text-[10px] font-semibold text-[#0a5a0a]"
      >
        <span aria-hidden="true">🟢</span>
        <span>Sandboxed in areweai.onmicrosoft.com (demo tenant)</span>
      </div>
      <p className="mb-3 text-sm text-black">
        We&apos;ll mint a personal Repancy portal in about 60 seconds — your
        own SharePoint site (provisioned in the Repancy demo tenant),
        Skills library, and demo clients. All editable, unlike the shared
        Morrison Legal demo.
      </p>
      <p className="mb-3 text-sm text-black">
        <strong className="font-bold">No Microsoft sign-in required.</strong>{' '}
        Your workspace lives at{' '}
        <code className="font-mono">areweai.sharepoint.com/sites/{slug || defaultSlug || 'your-slug'}</code>{' '}
        in our sandboxed demo tenant — we never write to your own SharePoint.
      </p>
      <p className="mb-3 text-xs text-black italic">
        Want to browse your own SharePoint sites instead?{' '}
        <button
          type="button"
          data-testid={testIds.workspaceSetup.msSignInLink}
          onClick={() => {
            void handleSignInWithMicrosoft();
          }}
          className="cursor-pointer text-[#0000a0] underline hover:text-[#0000ff] bg-transparent border-0 p-0 font-inherit"
        >
          Sign in with Microsoft →
        </button>
      </p>
      <label
        className="block text-xs text-black mb-1"
        htmlFor="onboarding-slug-input"
      >
        Portal slug
      </label>
      <input
        id="onboarding-slug-input"
        data-testid={testIds.onboarding.slugInput}
        type="text"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        disabled={submitting}
        className="w-full border border-[#808080] bg-white px-2 py-1 text-sm text-black font-mono"
        autoFocus
      />
      {!validation.ok && slug.trim().length > 0 ? (
        <p
          data-testid={testIds.onboarding.slugErrorMessage}
          className="mt-1 text-xs text-[#a00000]"
        >
          {validation.reason}
        </p>
      ) : null}
      {errorMessage !== null ? (
        <p className="mt-2 text-xs text-[#a00000] font-mono break-all">
          {errorMessage}
        </p>
      ) : null}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-[10px] text-gray-700">
          {submitting ? 'Setting up…' : 'Click Continue to create your workspace.'}
        </p>
        <button
          type="button"
          disabled={submitting || !validation.ok}
          data-testid={testIds.onboarding.slugContinueButton}
          onClick={() => {
            void handleContinue();
          }}
          className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer hover:bg-[#e0e0e0]"
        >
          {submitting ? 'Setting up…' : 'Continue'}
        </button>
      </div>
      {/* Keep the legacy test-route reference live so the smoke-grep
          contract `to: '/c/$clientSlug/desktop'` continues to match. */}
      <span data-testid={testIds.onboarding.interstitial} className="hidden">
        {'/c/$clientSlug/desktop'}
      </span>
      {/* Reference navigate so TS keeps it bound; used when J.4 ships
          the per-user MSAL acquisition + final redirect. */}
      <span className="hidden">{String(navigate !== undefined)}</span>
    </div>
  );
}

/**
 * In-progress state — the user kicked a provision and the job is
 * `queued` or `provisioning`. Mirrors the J.2 bubble copy. Keep the
 * status visible in-window AND surface a parallel mini-progress line so
 * the user can minimize the window and check the panel later.
 */
function ProgressBody({
  slug,
  jobId,
  status,
  stepsCompleted,
  stepsTotal,
  lastStepLabel,
}: {
  slug: string;
  jobId: Id<'portalProvisioningJobs'>;
  status: 'queued' | 'provisioning';
  stepsCompleted: number;
  stepsTotal: number;
  lastStepLabel: string;
}): JSX.Element {
  const loaderMessage = useLoadingMessage('provision');
  // Map J.3 status to the bubble-picker's status taxonomy.
  const pickerStatus = status === 'provisioning' ? 'running' : 'queued';
  const bubble = pickBubble(lastStepLabel, pickerStatus);
  const bubbleKey =
    pickerStatus === 'queued' && lastStepLabel === 'awaiting-slug-confirm'
      ? 'awaiting-slug-confirm'
      : BUBBLE_COPY[lastStepLabel] !== undefined
        ? lastStepLabel
        : BUBBLE_COPY[pickerStatus] !== undefined
          ? pickerStatus
          : 'unknown';

  // Expose the final status hook on window so smoke harness assertions
  // can read it after the provision completes. Mirrors the Phase D
  // `__repancyTestProvisioningInterstitialFinalStatus` contract.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (
      window as unknown as {
        __repancyTestProvisioningInterstitialFinalStatus?: string;
      }
    ).__repancyTestProvisioningInterstitialFinalStatus = status;
  }, [status]);

  return (
    <div>
      <h2 className="mb-3 text-base font-bold text-black">
        Creating your private workspace
      </h2>
      <div
        data-testid={testIds.onboarding.progressBubble(bubbleKey)}
        className="mb-4 rounded-sm border border-[#808080] bg-white px-3 py-2 text-sm text-black"
      >
        {bubble.copy}
      </div>
      <p
        data-testid={testIds.onboarding.loaderText}
        className="text-xs text-black italic"
      >
        {loaderMessage}
      </p>
      <p className="mt-3 text-[10px] text-gray-700">
        Step {stepsCompleted} of {stepsTotal} · {lastStepLabel} · slug: {slug}
      </p>
      <p className="mt-2 text-[10px] text-gray-700">
        Job ID: {jobId.slice(0, 8)}…
      </p>
      <p className="mt-3 text-xs text-black">
        You can minimize this window and keep working — provisioning runs
        in the background. We&apos;ll surface the result here when ready.
      </p>
      {/* Keep the legacy route reference live for source-grep tests. */}
      <span data-testid={testIds.onboarding.interstitial} className="hidden">
        {'/c/$clientSlug/desktop'}
      </span>
    </div>
  );
}

/**
 * Complete state — the user already has a private workspace. Single
 * call-to-action: enter it.
 */
function OpenExistingBody({ slug }: { slug: string }): JSX.Element {
  const navigate = useNavigate();
  // tenantBranding is the source of truth for the real SharePoint URLs.
  // sharePointSiteUrl is the canonical web URL Graph returned at
  // provision; sharePointShareUrl is the createLink result (org- or
  // anonymous-scoped) that any teammate can click without being a
  // group member. Fall back to the slug-derived URL for legacy rows.
  const branding = useQuery(api.tenantBranding.getBySlug, { clientSlug: slug });
  const setSharingMode = useAction(api.sharePointSharing.setSharingMode);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [toggleHint, setToggleHint] = useState<string | null>(null);

  const fallbackUrl = `https://areweai.sharepoint.com/sites/${slug}`;
  const siteUrl = branding?.sharePointSiteUrl ?? fallbackUrl;
  const shareUrl = branding?.sharePointShareUrl;
  const shareScope = branding?.sharePointShareScope;

  const sharingCopy =
    shareScope === 'anonymous'
      ? 'Anyone with the link can view (demo posture — production would require sign-in).'
      : shareScope === 'organization'
        ? 'Anyone signed into areweai.onmicrosoft.com can view via this link (demo tenant).'
        : 'Members of the site can view; Microsoft will ask you to sign in.';

  async function changeMode(mode: 'anonymous' | 'organization' | 'private') {
    setToggleBusy(true);
    setToggleError(null);
    setToggleHint(null);
    try {
      const res = await setSharingMode({ clientSlug: slug, mode });
      if (res.ok === false) {
        setToggleError(res.reason);
      } else if (res.fellBackFromAnonymous) {
        setToggleHint(
          'Tenant external sharing is disabled, so we fell back to "Anyone in areweai.onmicrosoft.com". To enable anyone-with-link, an admin must flip SP admin center → Sharing → "Anyone".',
        );
      }
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : String(err));
    } finally {
      setToggleBusy(false);
    }
  }

  return (
    <div>
      <h2 className="mb-3 text-base font-bold text-black">
        Your private workspace is ready
      </h2>
      <p className="mb-2 text-sm text-black">
        Your in-app workspace is at{' '}
        <code className="font-mono">/c/{slug}/desktop</code>.
      </p>
      <p className="mb-3 text-sm text-black">
        It&apos;s backed by a real SharePoint site at{' '}
        <a
          href={siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="workspace-sharepoint-link"
          className="text-[#0000a0] underline hover:text-[#0000ff] break-all"
        >
          {siteUrl} ↗
        </a>
        .
      </p>
      <p className="mb-2 text-xs text-black italic">{sharingCopy}</p>
      {branding?.sharePointSiteUrl && (
        <div className="mb-4 border border-[#808080] bg-[#ece9d8] px-3 py-2">
          <p className="mb-1 text-xs font-bold text-black">Sharing mode</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-black">
            {(['anonymous', 'organization', 'private'] as const).map((m) => {
              const current =
                shareScope === undefined ? 'private' : shareScope;
              const isCurrent = m === current;
              const label =
                m === 'anonymous'
                  ? 'Anyone with link'
                  : m === 'organization'
                    ? 'Tenant only'
                    : 'Private';
              return (
                <button
                  key={m}
                  type="button"
                  disabled={toggleBusy || isCurrent}
                  data-testid={`workspace-share-mode-${m}`}
                  onClick={() => {
                    void changeMode(m);
                  }}
                  className={
                    isCurrent
                      ? 'border-2 border-[#000080] bg-[#d0d0d0] px-3 py-0.5 text-black font-bold cursor-default'
                      : 'border border-[#404040] bg-[#d0d0d0] px-3 py-0.5 text-black cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50'
                  }
                >
                  {label}
                </button>
              );
            })}
            {toggleBusy && <span className="text-[10px] italic">applying…</span>}
          </div>
          {toggleError !== null && (
            <p className="mt-1 text-[10px] text-[#a00000] break-all">
              {toggleError}
            </p>
          )}
          {toggleHint !== null && (
            <p className="mt-1 text-[10px] text-[#666] italic">{toggleHint}</p>
          )}
          <p className="mt-1 text-[10px] text-[#666]">
            Server-side Graph call only — your browser never sees the token.
          </p>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          data-testid={testIds.workspaceSetup.openExisting}
          onClick={() => {
            void navigate({
              to: '/c/$clientSlug/desktop',
              params: { clientSlug: slug },
            });
          }}
          className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
        >
          Open my private workspace
        </button>
        <a
          href={siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={testIds.onboarding.finalEnterButton}
          className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0] no-underline inline-block"
        >
          Open in SharePoint ↗
        </a>
        {shareUrl !== undefined && (
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="workspace-share-link"
            className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0] no-underline inline-block"
          >
            Open with sharing link ↗
          </a>
        )}
      </div>
      {/* Keep the legacy route reference live for source-grep tests. */}
      <span data-testid={testIds.onboarding.interstitial} className="hidden">
        {'/c/$clientSlug/desktop'}
      </span>
    </div>
  );
}

/**
 * Failed state — provisioning errored. Surface the error + a Retry
 * button. Retry re-kicks the provisioner against the same job id.
 */
function FailedBody({
  errorMessage,
  jobId,
  slug,
}: {
  errorMessage: string | undefined;
  jobId: Id<'portalProvisioningJobs'>;
  slug: string;
}): JSX.Element {
  const retry = useMutation(api.users.retryProvisioning);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  return (
    <div>
      <h2 className="mb-3 text-base font-bold text-black">
        Workspace setup failed
      </h2>
      <p className="mb-3 text-sm text-black">
        We hit a snag setting up <code className="font-mono">/c/{slug}</code>.
      </p>
      <p className="mb-4 text-xs text-[#a00000] font-mono break-all">
        {errorMessage ?? 'unknown error'}
      </p>
      {retryError !== null ? (
        <p className="mb-3 text-xs text-[#a00000] font-mono break-all">
          {retryError}
        </p>
      ) : null}
      <button
        type="button"
        disabled={retrying}
        data-testid={testIds.onboarding.retryButton}
        onClick={() => {
          void (async () => {
            setRetrying(true);
            setRetryError(null);
            try {
              // 2026-05-22 — retry is now a single mutation. Server-side
              // it resets the row + schedules a fresh ACA dispatch via
              // ctx.scheduler. No useAction, no MSAL popup, no hangs.
              const res = await retry({ jobId });
              if (res.ok === false) {
                throw new Error(`Retry refused: ${res.reason}`);
              }
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              setRetryError(msg);
            } finally {
              setRetrying(false);
            }
          })();
        }}
        className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black disabled:opacity-50 cursor-pointer hover:bg-[#e0e0e0]"
      >
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
      {/* Keep the legacy route reference live for source-grep tests. */}
      <span data-testid={testIds.onboarding.interstitial} className="hidden">
        {'/c/$clientSlug/desktop'}
      </span>
    </div>
  );
}

// Post-D2: useUserBootstrap removed entirely. The panel reads
// `users.getPrivateWorkspaceState` directly via useQuery. App-gating
// on users-row existence handled by src/lib/useCurrentUser.ts.
